import React from "react";
import { FileText, Sparkles, CalendarClock, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

// Three tracks, distinguished by where the knowledge comes from.
//
// The separate "Knowledge Base (Confluence)" card is gone. Confluence feeds
// the topics inside the general track instead, so a refund session mixes
// hand-written scenarios with generated coverage rather than splitting the
// same knowledge across two places.
const TRACKS = [
  {
    key: "report",
    title: "Training based on your report",
    desc: "A focused session built from your latest QA report — refunds, escalations, and compliance gaps.",
    icon: FileText,
    accent: "primary",
  },
  {
    key: "general",
    title: "Knowledge check",
    desc: "Policy and process, by topic — billing, refunds, legal, systems, products and more. Kept current from the knowledge base.",
    icon: Sparkles,
    accent: "accent",
  },
  {
    key: "monthly",
    title: "Monthly test (Slack updates)",
    desc: "What changed this month, drawn from the announcement channels so nothing gets missed.",
    icon: CalendarClock,
    accent: "accent",
  },
];

export default function TrainingSelect({
  reportCount,
  generalCount,
  monthlyCount,
  onSelect,
}) {
  const counts = {
    report: reportCount,
    general: generalCount,
    monthly: monthlyCount,
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-12 sm:py-20">
      <div className="text-center mb-10">
        <span className="text-xs font-medium tracking-widest uppercase text-primary">
          Training Session
        </span>
        <h1 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-foreground">
          Customer Support Training Arena
        </h1>
        <p className="mt-4 text-base sm:text-lg leading-relaxed text-muted-foreground">
          Choose a track to begin. Each session takes about 2–3 minutes and
          gives you instant feedback.
        </p>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        {TRACKS.map((t) => {
          const count = counts[t.key] ?? 0;
          const empty = count === 0;

          return (
            <button
              key={t.key}
              type="button"
              onClick={() => onSelect(t.key)}
              disabled={empty && t.key !== "general"}
              className={cn(
                "group text-left p-6 rounded-2xl border bg-card transition-all duration-200",
                empty && t.key !== "general"
                  ? "border-border opacity-60 cursor-not-allowed"
                  : "hover:-translate-y-0.5 hover:shadow-lg",
                !empty && t.accent === "primary"
                  ? "border-primary/30 hover:border-primary"
                  : !empty
                  ? "border-accent/30 hover:border-accent"
                  : "border-border"
              )}
            >
              <div
                className={cn(
                  "inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4",
                  t.accent === "primary"
                    ? "bg-primary/10 text-primary"
                    : "bg-accent/10 text-accent"
                )}
              >
                <t.icon className="w-6 h-6" strokeWidth={1.5} />
              </div>

              <h2 className="text-lg font-semibold text-foreground leading-snug">
                {t.title}
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {t.desc}
              </p>

              <div className="mt-5 flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">
                  {t.key === "general"
                    ? "Choose a topic"
                    : count > 0
                    ? `${count} questions`
                    : "Nothing here yet"}
                </span>
                {(!empty || t.key === "general") && (
                  <span
                    className={cn(
                      "inline-flex items-center text-sm font-medium",
                      t.accent === "primary" ? "text-primary" : "text-accent"
                    )}
                  >
                    Start
                    <ArrowRight className="ml-1.5 w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
